import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import AppContext, { Role } from '../AppContext';
import { Organization } from '../viewModels/Organization';
import { mapToOrganizationViewmodel } from './mappers/organization';
import { GetOrganizationPayload } from './generated/types';

export interface useGetOrganizationResult {
    maintainableRoles: Array<Role>;
    organization: Organization;
}

export const useGetOrganization = (id: string) => {
    const context = useContext(AppContext);

    const query = `query GetOrganization($id: ID!) {
          getOrganization(id: $id) {
            maintainableRoles
            organization {
              address
              city
              code
              contactEmail
              contactPerson
              contactPhone
              country
              id
              name
              registration
              zip
              isAllowedToUseLicenseSystemsOffline
              isHeadOrganization
              parentOrgId
              parentOrgData {
                id
                name
              }
              licenses {
                module
                counters {
                    totalNumberInContracts
                    availableNumber
                }
                contractStatus {
                    hasContract
                    nearestExpiringContract {
                        hasExpired
                        autoRenewal
                        expirationDate
                    }
                }
                seats {
                    totalNumberInContracts
                    numberAssignedToUsers
                    isUnlimited
                    availableNumber
                }
              }
              users {
                email
                firstName
                lastName
                id
                role
                moduleIds
                groupNames
                invite {
                  status
                }
                lastActivityDate
              }
              groups {
                folderCount
                id
                userCount
                name
              }
              rootFolders {
                id
                name
              }
              licenseSystems {
                id
                hasPendingProcess
                licenseSystemType
                computerName
                location
                isTransferSupported
                isLicenseSystemUpdateAvailable
                lastSeenDate
                isSucActive
                cpGo {
                    isLicensed
                    isOnline
                }
                admin {
                    email
                    username
                }
                licenses {
                    name
                    expirationDate
                    productLine
                    installedApplication {
                        version
                        lastSeenDate
                    }
                    options {
                      name
                      expirationDate
                    }
                  }
              }
            }
          }
        }
      `;

    return useQuery<useGetOrganizationResult, Error>({
        queryKey: ['getOrganization', id],
        enabled: !!id,
        queryFn: async () => {

            const response = await (context.api.graphql({ query, variables: { id } }) as Promise<{
                data: {
                    getOrganization: GetOrganizationPayload;
                };
            }>);
            const { maintainableRoles, organization } = response.data.getOrganization;

            return {
                organization: mapToOrganizationViewmodel(organization!),
                maintainableRoles,
            };
        },
    });
};
